/**
 * Inställningar › Automatisk tilldelning: the felanmälan setting and the
 * förvaltare's link status from the callable, and its save.
 */
import { useCallback, useEffect, useState } from 'react';
import { autoAssignmentApi } from '../../api/autoAssignmentApi';
import { IssueAutoAssignmentState, SaveIssueAutoAssignmentPayload } from '../../types/autoAssignment';
import { setCachedIssueAutoAssignment } from './useIssueAutoAssignment';

export interface UseAutoAssignmentSettingsReturn extends IssueAutoAssignmentState {
    loading: boolean;
    saving: boolean;
    error: string | null;
    save: (payload: SaveIssueAutoAssignmentPayload) => Promise<void>;
}

const message = (err: unknown, fallback: string) =>
    err instanceof Error && err.message ? err.message : fallback;

export const useAutoAssignmentSettings = (): UseAutoAssignmentSettingsReturn => {
    const [state, setState] = useState<IssueAutoAssignmentState>({ setting: null, link: null });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        autoAssignmentApi.getIssues()
            .then((s) => { if (!cancelled) setState(s); })
            .catch((err) => {
                console.error(err);
                if (!cancelled) setError(message(err, 'Kunde inte läsa inställningen'));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const save = useCallback(async (payload: SaveIssueAutoAssignmentPayload) => {
        setSaving(true);
        setError(null);
        try {
            const next = await autoAssignmentApi.saveIssues(payload);
            setState(next);
            setCachedIssueAutoAssignment(next.setting);
        } catch (err) {
            setError(message(err, 'Kunde inte spara inställningen'));
            throw err;
        } finally {
            setSaving(false);
        }
    }, []);

    return { ...state, loading, saving, error, save };
};
